import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { ASSETS, LINKS, SITE_ORIGIN } from "../lib/constants";
import Footer from "../components/Footer";
import useBreadcrumbJsonLd from "../lib/useBreadcrumbJsonLd";
import { getPostBySlug } from "../data/blog";
import { getPaperBySlug } from "../data/methodology";

/*
 * Blog post renderer. Reads the slug from the URL, looks up the post in
 * the manifest, and renders the article body + JSON-LD (Article + nested
 * FAQPage) for search engines and AI crawlers.
 */

function useBlogMeta(post) {
    useEffect(() => {
        if (!post) return;
        window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });

        document.title = post.seoTitle || post.title;

        const canonical = `${SITE_ORIGIN}/blog/${post.slug}`;
        const set = (selector, attr, value) => {
            const el = document.querySelector(selector);
            if (el) el.setAttribute(attr, value);
        };
        set('meta[name="description"]', "content", post.metaDescription || post.description);
        set('meta[property="og:title"]', "content", post.title);
        set(
            'meta[property="og:description"]',
            "content",
            post.ogDescription || post.description
        );
        set('meta[property="og:type"]', "content", "article");
        set('meta[property="og:url"]', "content", canonical);
        set('meta[name="twitter:title"]', "content", post.title);
        set(
            'meta[name="twitter:description"]',
            "content",
            post.ogDescription || post.description
        );
        set('link[rel="canonical"]', "href", canonical);
    }, [post]);
}

// JSON-LD payload builders. Kept out of the render tree as JSON strings
// so we can embed them inside <script type="application/ld+json"> via
// dangerouslySetInnerHTML — which the prerender then bakes into the
// static HTML.
function buildArticleJsonLd(post) {
    const payload = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.description,
        author: { "@type": "Organization", name: "TRT Plus" },
        publisher: {
            "@type": "Organization",
            name: "TRT Plus",
            logo: {
                "@type": "ImageObject",
                url: `${SITE_ORIGIN}/og-image.jpg`,
            },
        },
        datePublished: post.isoDate,
        dateModified: post.isoModified || post.isoDate,
        mainEntityOfPage: `${SITE_ORIGIN}/blog/${post.slug}`,
        articleSection: post.category,
    };
    if (post.technicalNoteSlugs && post.technicalNoteSlugs.length > 0) {
        payload.citation = post.technicalNoteSlugs
            .map(getPaperBySlug)
            .filter(Boolean)
            .map((p) => ({
                "@type": "ScholarlyArticle",
                "@id": `https://doi.org/${p.doi}`,
                name: p.title,
                identifier: p.doi,
                sameAs: p.url,
                url: p.url,
            }));
    }
    return JSON.stringify(payload);
}

function buildFaqJsonLd(post) {
    if (!post.faq || post.faq.length === 0) return null;
    return JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    });
}

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPostBySlug(slug);

    useBlogMeta(post);
    useBreadcrumbJsonLd(
        post
            ? [
                  { name: "Home", url: `${SITE_ORIGIN}/` },
                  { name: "Blog", url: `${SITE_ORIGIN}/blog` },
                  { name: post.title, url: `${SITE_ORIGIN}/blog/${post.slug}` },
              ]
            : null
    );

    if (!post) {
        return (
            <main className="min-h-screen bg-black text-white flex items-center justify-center p-6">
                <div className="text-center">
                    <h1 className="font-display text-3xl mb-4">
                        Article not found
                    </h1>
                    <Link to="/blog" className="text-[#49bef5] hover:underline">
                        ← Back to blog
                    </Link>
                </div>
            </main>
        );
    }

    const Body = post.Component;
    const articleLd = buildArticleJsonLd(post);
    const faqLd = buildFaqJsonLd(post);

    return (
        <main
            data-testid="page-blog-post"
            className="relative bg-black text-[#f4f5f7] font-body min-h-screen"
        >
            {/* JSON-LD — Article + nested FAQPage. Placed inline so the
                prerender captures them into the static HTML. */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: articleLd }}
            />
            {faqLd && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: faqLd }}
                />
            )}
            {/* slim header */}
            <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
                    <Link
                        to="/"
                        data-testid="blog-home-link"
                        className="flex items-center gap-2.5 text-white"
                    >
                        <img
                            src={ASSETS.icon}
                            alt="TRT Plus"
                            className="h-8 w-8 rounded-md"
                        />
                        <span className="font-brand text-2xl leading-none">
                            <span className="text-white">TRT</span>
                            <span className="text-[#2ba6e0]">+</span>
                        </span>
                    </Link>
                    <Link
                        to="/blog"
                        data-testid="blog-back-link"
                        className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-white transition-colors"
                    >
                        <ArrowLeft size={16} /> Back to blog
                    </Link>
                </div>
            </header>

            {/* hero */}
            <section className="relative pt-20 lg:pt-28 pb-12 lg:pb-16 overflow-hidden border-b border-white/5">
                <div className="absolute inset-0 grid-backdrop opacity-30 pointer-events-none" />
                <div className="absolute inset-0 radial-glow opacity-60 pointer-events-none" />
                <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5">
                        <span className="dot" /> {post.category}
                    </div>
                    <h1
                        className="font-display font-bold text-white leading-tight"
                        style={{
                            fontSize: "clamp(2rem, 4.5vw, 3.25rem)",
                            letterSpacing: "-0.02em",
                        }}
                        data-testid="blog-title"
                    >
                        {post.title}
                    </h1>
                    <p className="mt-5 text-sm font-mono text-white/45 uppercase tracking-widest">
                        {post.date} · {post.readingTime}
                    </p>
                </div>
            </section>

            {/* body */}
            <article className="relative py-16 lg:py-20">
                <div className="max-w-3xl mx-auto px-6 lg:px-10 legal-prose">
                    <Body />

                    {post.related && post.related.length > 0 && (
                        <>
                            <div className="hairline my-12" />
                            <div data-testid="blog-related">
                                <div className="text-[11px] uppercase tracking-widest font-mono text-[#49bef5] mb-3">
                                    Related reading
                                </div>
                                <ul className="space-y-2 text-white/80">
                                    {post.related.map((r) => (
                                        <li key={r.to}>
                                            <Link
                                                to={r.to}
                                                className="text-[15px] hover:text-white"
                                            >
                                                → {r.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </>
                    )}

                    <div className="hairline my-12" />
                    <div
                        className="tile p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
                        data-testid="blog-contact-card"
                    >
                        <div>
                            <div className="text-[11px] uppercase tracking-widest font-mono text-[#49bef5] mb-1">
                                Questions on the engine?
                            </div>
                            <div className="text-white/80 text-[15px]">
                                Every email gets a personal reply from the
                                developer.
                            </div>
                        </div>
                        <a
                            href={LINKS.email}
                            className="btn-blue inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"
                        >
                            <Mail size={16} /> support@trtplus.app
                        </a>
                    </div>
                </div>
            </article>

            <Footer />
        </main>
    );
}
